import React, { useState } from "react";
import PropTypes from "prop-types";
import ColoredLogo from "../../ColoredLogo";
import SectionCard from "../../profiles/SectionCard";
import SectionSkeleton from "../../profiles/SectionSkeleton";
import PaginationGroup from "../../profiles/PaginationGroup";

const STORY_IDEAS_PER_PAGE = 5;
const VISITOR_SUMMARY = "Generated by Qwoted based on recent pitches.";
const EDITOR_SUMMARY = `${VISITOR_SUMMARY} These story ideas help boost your visibility and increase the chances of direct outreach from reporters.`;

/**
 * StoryIdeasCard — chrome half of the Expert/Hybrid profile's "Story Ideas" section (the data
 * half is use_story_ideas). Renders the enrich-scored `public_pitches` examples that named this
 * expert as a pitched target: each pitch's title and body text, five to a page, under a summary
 * line that tells viewers who can edit the profile what the ideas are for.
 *
 * Content, not an owner-only widget: self-collapses on empty for every viewer, with no
 * editable-viewer exception — unlike Quotes/Media, there is nothing here for an owner to "add".
 * These are pitches the platform surfaced as scored examples, not something the profile owner
 * authored, so a sparse profile simply has no card rather than an empty shell inviting input.
 *
 * @param {Object} props
 * @param {Array} props.pitches - Raw `public_pitches` view rows (`id`, `title`, `text`, ...)
 * @param {boolean} [props.isLoading] - True while the pitch read is in flight
 * @param {boolean} [props.editable] - Whether the viewer may edit this profile (longer summary line)
 */
function StoryIdeasCard({ pitches, isLoading = false, editable = false }) {
  const [page, setPage] = useState(1);

  if (isLoading) return <SectionSkeleton lines={3} />;
  if (!pitches.length) return null;

  const pageCount = Math.ceil(pitches.length / STORY_IDEAS_PER_PAGE);
  const currentPage = Math.min(page, pageCount);
  const visiblePitches = pitches.slice((currentPage - 1) * STORY_IDEAS_PER_PAGE, currentPage * STORY_IDEAS_PER_PAGE);

  return (
    <SectionCard id="expert-section-story-ideas" title="Story Ideas">
      <div className="story-ideas-box border border-accent-blue-20">
        <div className="story-ideas-summary d-flex align-items-start">
          <ColoredLogo className="flex-shrink-0" size={36} color="#f75540" />
          <div>
            <div className="paragraph-md fw-bold">Summary</div>
            <div className="paragraph-sm lh-sm text-secondary">{editable ? EDITOR_SUMMARY : VISITOR_SUMMARY}</div>
          </div>
        </div>

        <ul className="list-unstyled mb-0">
          {visiblePitches.map((pitch) => (
            <li key={pitch.id} className="story-idea">
              <div className="story-idea-title paragraph-md fw-bold">{pitch.title}</div>
              <div className="paragraph-sm text-secondary">{pitch.text}</div>
            </li>
          ))}
        </ul>
      </div>

      <PaginationGroup
        className="story-ideas-pager"
        page={currentPage}
        pageCount={pageCount}
        onPageChange={setPage}
        ariaLabel="Story Ideas pages"
      />
    </SectionCard>
  );
}

StoryIdeasCard.propTypes = {
  pitches: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      text: PropTypes.string,
    }),
  ).isRequired,
  isLoading: PropTypes.bool,
  editable: PropTypes.bool,
};

export default StoryIdeasCard;
